/* eslint-disable max-lines */
import React, { FocusEvent, FC, useCallback, useState, useEffect, useContext, ReactElement } from 'react'
import { FieldHookConfig, useField } from 'formik'
import { useTranslations } from 'next-intl'
import { codeValidator } from '../../validator/useFieldValidators'
import { checkPhoneValid, formatPhoneValue, getValidationOptions } from '../PhoneInput2/utils'
import { getDigits } from '../../utils/common'
import IFormValues, { IFormValue, Validate } from '../../validator/IFormValues'
import { PHONE_SMS_CODE_MIN_LENGTH, PHONE_CALL_CODE_MIN_LENGTH } from '../../validator/constants'
import { PHONE_ALREADY_EXISTS_TEXT } from '../formComponents/constants'
import { IError, Languages } from '../../interfaces/common'
import { FormFieldTypes } from '../../emun/RegistrationFormFieldTypes'
import { IConfirmedPhone } from '../formComponents/FormPhoneField/interfaces/FormPhoneFieldInterfaces'
import { useEnv } from '../../utils/useEnv'
import { LocaleContext } from '../LocaleContext/LocaleContext'
import { TOO_EARLY, TO_MANY_REQUESTS_STATUS } from '../../constants/errors'
import { PhoneInput2 } from '../PhoneInput2/PhoneInput2'

import { useAppDispatch } from '../../store'
import { setIsFocused } from '../../store/slices/focuseInputSlice'
import { usePhoneCheck } from '../../api/mutations/usePhoneСheck'

const GENERATE_CODE_TIME_ERROR = 'app.errors.The previous active code was generated less than a minute ago'
const TRY_AFTER_HOUR_ERROR = 'app.errors.Try after 1 hour'

type IPhoneFieldProps<IInitialValues = IFormValues> = {
  onChange?(data: FocusEvent<HTMLInputElement, Element>): void
  onFocus?(data: FocusEvent<HTMLInputElement, Element>): void
  onBlur?(data: FocusEvent<HTMLInputElement, Element>): void
  sendCode(phone: string, isCall?: boolean): Promise<void>
  isSubmitButtonDisabledChange?(value: boolean): void
  sendCodeBlurWrapper(smsCode: string, locale: Languages): void
  initialValues?: IInitialValues
  label: string | null | ReactElement
  name: string
  hasSmallMarginBottom?: boolean
  immediatelyShowCodeInput: boolean
  isPhoneSubmitted: boolean
  errorDataQaLocator?: string
  withCode?: boolean
  onlySmsCode?: boolean
  doNotShowBusy?: boolean
  isShowCodeRegFormInput?: boolean
  isCallCode: boolean
  appidQueryParam?: string
  formQueryParam?: string
  previousSendCodePhone?: string
  language: Languages
  codeFormError?: string
  codeType: FormFieldTypes
  eventGroup?: string
  shortForm?: boolean
  isCallCodeChange?(value: boolean): void
  codeFormErrorChange?(error: string): void
  previousSendCodePhoneChange?(phone: string): void
  validator?: Validate
  setIsPhoneConfirmed?: (error: boolean) => void
  isShowCodeRegFormInputChange?(value: boolean): void
  setPhoneIsConfirmed?(value: IConfirmedPhone): void
} & FieldHookConfig<string>

export const PhoneField: FC<IPhoneFieldProps> = ({
  onChange,
  onFocus,
  onBlur,
  validator,
  sendCode,
  sendCodeBlurWrapper,
  previousSendCodePhoneChange,
  codeFormErrorChange,
  isCallCodeChange,
  isSubmitButtonDisabledChange,
  setIsPhoneConfirmed,
  isShowCodeRegFormInputChange,
  setPhoneIsConfirmed,
  eventGroup,
  shortForm,
  codeFormError,
  label,
  codeType,
  previousSendCodePhone,
  hasSmallMarginBottom,
  errorDataQaLocator,
  withCode,
  onlySmsCode,
  language,
  appidQueryParam,
  doNotShowBusy,
  initialValues,
  formQueryParam,
  immediatelyShowCodeInput,
  isPhoneSubmitted,
  isCallCode,
  isShowCodeRegFormInput,
  ...props
}) => {
  const [errorText, errorTextChange] = useState<string>('')
  const [isPhoneBusy, isPhoneBusyChange] = useState<boolean>(false)
  const [isShowCodeInput, isShowCodeInputChange] = useState<boolean>(immediatelyShowCodeInput)
  const [isCodeInputDisabled, isCodeInputDisabledChange] = useState<boolean>(true)
  const deployEnv = useEnv()
  const t = useTranslations()
  const { locale } = useContext(LocaleContext)
  const phoneСheck = usePhoneCheck()
  const dispatch = useAppDispatch()

  const validatorWrapper = (name: string) => (currentValue: IFormValue) => {
    if (isPhoneBusy) {
      return Promise.resolve(PHONE_ALREADY_EXISTS_TEXT)
    }

    return validator && validator(name)(currentValue)
  }

  const [phoneField, phoneMeta, phoneHelper] = useField({
    ...props,
    validate: validatorWrapper(props.name),
  })

  const getCodeValidator = () => {
    if (codeFormError) {
      return () => Promise.resolve(codeFormError)
    }

    if (errorText) {
      return () => Promise.resolve(errorText)
    }

    return (value: string) => Promise.resolve(codeValidator(value, isCallCode))
  }

  const [codeField, codeMeta, codeHelper] = useField({
    name: codeType,
    ...(!isPhoneSubmitted && withCode ? { validate: getCodeValidator() } : {}),
  })

  const { name, value } = phoneField

  const onFocusHandler = (e: FocusEvent<HTMLInputElement, Element>) => {
    if (!phoneField.value || phoneField.value.trim() === '') {
      phoneHelper.setValue('+')
    }

    if (onFocus) onFocus(e)
  }

  const onCodeFocusHandler = () => {
    codeHelper.setTouched(false)
  }

  const onBlurHandler = useCallback(
    (e: FocusEvent<HTMLInputElement, Element>) => {
      if (onBlur) onBlur(e)

      if (phoneField.value === '+') {
        phoneHelper.setValue('')
      }

      phoneField.onBlur(e)
    },
    [onBlur, phoneField],
  )

  useEffect(() => {
    if (withCode) {
      const isSubmitButtonDisable =
        (isCallCode && codeField.value.length < PHONE_CALL_CODE_MIN_LENGTH) ||
        (!isCallCode && codeField.value.length < PHONE_SMS_CODE_MIN_LENGTH)

      if (isSubmitButtonDisable && isSubmitButtonDisabledChange && !isPhoneSubmitted) {
        isSubmitButtonDisabledChange(true)
      } else if (isSubmitButtonDisabledChange) {
        isSubmitButtonDisabledChange(false)
      }
    }
  }, [codeField.value, isCallCode, isPhoneSubmitted, withCode, value, isSubmitButtonDisabledChange])

  useEffect(() => {
    if (codeFormError) {
      codeHelper.setTouched(true, true)
    }
  }, [codeFormError, codeHelper])

  useEffect(() => {
    if (isPhoneBusy) {
      phoneHelper.setTouched(true, true)
      phoneHelper.setError(PHONE_ALREADY_EXISTS_TEXT)
    }
  }, [isPhoneBusy, phoneHelper])

  useEffect(() => {
    if (errorText) {
      codeHelper.setTouched(true, true)
    }
  }, [codeHelper, errorText])

  const onCodeBlurHandler = useCallback(
    ({ target, relatedTarget }: FocusEvent<HTMLInputElement>) => {
      const error = codeValidator(target.value, isCallCode)

      if (codeFormErrorChange) {
        codeFormErrorChange('')
      }

      if (!error) {
        sendCodeBlurWrapper(target.value, locale)
      }

      errorTextChange(error)

      if (!relatedTarget || relatedTarget.tagName !== 'BUTTON') {
        codeHelper.setError(error)
        setTimeout(() => codeHelper.setTouched(true, true), 0)
      }
    },
    [isCallCode, codeFormErrorChange, sendCodeBlurWrapper, locale, codeHelper],
  )

  const onCodeChangeHandler = ({ target }: FocusEvent<HTMLInputElement, Element>) => {
    if (codeFormErrorChange) codeFormErrorChange('')
    codeHelper.setValue(target.value)
  }

  const onCodeClickHandler = useCallback(
    (success: () => void, fail?: () => void, isCall?: boolean) => {
      errorTextChange('')
      codeHelper.setValue('')
      codeHelper.setError('')
      codeHelper.setTouched(false)

      if (isCallCodeChange) {
        isCallCodeChange(Boolean(isCall))
      }

      sendCode(value, isCall)
        .then(() => {
          isCodeInputDisabledChange(false)
          success()
        })
        .catch((e: IError) => {
          if (e.response?.status === TO_MANY_REQUESTS_STATUS) {
            isCodeInputDisabledChange(false)
            if (codeFormErrorChange) codeFormErrorChange(t(GENERATE_CODE_TIME_ERROR))
            codeHelper.setTouched(true, true)
          }

          if (e.response?.status === TOO_EARLY) {
            if (codeFormErrorChange) codeFormErrorChange(t(TRY_AFTER_HOUR_ERROR))
            codeHelper.setTouched(true, true)
          }

          if (fail) {
            fail()
          }

          return e
        })
    },
    [codeHelper, isCallCodeChange, sendCode, value, codeFormErrorChange, t],
  )

  const checkUsedPhone = useCallback(
    (phoneValue: string, phone = ''): boolean => {
      const appropriateOption = getValidationOptions(phone, deployEnv)
      const formattedValue = formatPhoneValue(phone, appropriateOption)

      return phoneValue === formattedValue
    },
    [deployEnv],
  )

  const phoneBusy = useCallback(
    async (phone: string, callbackSuccess?: () => void) => {
      const appropriateOption = getValidationOptions(phone, deployEnv)
      const formattedValue = formatPhoneValue(phone, appropriateOption)
      const isPhoneValid = checkPhoneValid(formattedValue, deployEnv, locale)
      const isUsedPhone = checkUsedPhone(formattedValue, initialValues?.phone)

      if (isPhoneValid && !isUsedPhone) {
        setIsPhoneConfirmed?.(true)

        try {
          const { isAvailable, isValid } = await phoneСheck({
            locale,
            phone: getDigits(phone),
          })

          if (!isAvailable) {
            return true
          }

          if (isAvailable && isValid && callbackSuccess) {
            callbackSuccess()
          }
        } catch (error) {
          console.error(error)
        }
      }

      return false
    },
    [checkUsedPhone, deployEnv, initialValues?.phone, locale, phoneСheck, setIsPhoneConfirmed],
  )

  const onChangeHandler = useCallback(
    async (phone: string) => {
      if (codeFormErrorChange) codeFormErrorChange('')

      const previousValue = phoneMeta.value

      if (previousSendCodePhoneChange) {
        previousSendCodePhoneChange(previousValue)
      }

      phoneHelper.setValue(phone)

      if (phone !== previousValue) {
        isPhoneBusyChange(false)
        isShowCodeInputChange(false)
        isShowCodeRegFormInputChange?.(false)
        isCodeInputDisabledChange(true)
        errorTextChange('')
        if (previousSendCodePhoneChange) previousSendCodePhoneChange('')
      }

      if (checkPhoneValid(phone, deployEnv, locale)) {
        const phoneIsBusy = await phoneBusy(phone)

        if (phoneIsBusy) {
          isShowCodeInputChange(false)
          isPhoneBusyChange(true)
          dispatch(setIsFocused(false))
          phoneHelper.setTouched(true, true)
          phoneHelper.setError(PHONE_ALREADY_EXISTS_TEXT)
        } else {
          isShowCodeInputChange(true)
          isShowCodeRegFormInputChange?.(true)
        }
      }
    },
    [
      codeFormErrorChange,
      codeHelper,
      deployEnv,
      isShowCodeRegFormInputChange,
      locale,
      phoneBusy,
      phoneHelper,
      phoneMeta.value,
      previousSendCodePhoneChange,
      setPhoneIsConfirmed,
    ],
  )

  return (
    <PhoneInput2
      eventGroup={eventGroup}
      name={name}
      value={value}
      isCallCode={isCallCode}
      codeFieldValue={codeField.value}
      onChange={onChangeHandler}
      onFocus={onFocusHandler}
      onBlur={onBlurHandler}
      onCodeClick={onCodeClickHandler}
      onCodeFocus={onCodeFocusHandler}
      onCodeBlur={onCodeBlurHandler}
      onCodeChange={onCodeChangeHandler}
      isShowCodeRegFormInput={isShowCodeRegFormInput}
      label={label}
      isPhoneSubmitted={isPhoneSubmitted}
      onlySmsCode={onlySmsCode}
      isShowCodeInput={isShowCodeInput && withCode}
      error={phoneMeta.error}
      touched={phoneMeta.touched}
      isPhoneBusy={!doNotShowBusy && isPhoneBusy}
      login={phoneMeta.value}
      isCodeInputDisabled={isCodeInputDisabled}
      codeError={codeMeta.touched ? codeMeta.error : null}
    />
  )
}

PhoneField.displayName = 'PhoneField'
